import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

const client = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000
});

// Add auth token to requests
client.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Types
export interface EmailActivityEvent {
  type: string;
  at: string;
}

export interface EmailActivityItem {
  id: string;
  messageId: string | null;
  templateId: string | null;
  templateName: string | null;
  recipient: string | null;
  from: string | null;
  subject: string | null;
  tags: string[];
  sentAt: string | null;
  status: string | null;
  opened: boolean;
  clicked: boolean;
  unsubscribed: boolean;
  spamComplaint: boolean;
  events: EmailActivityEvent[];
  claimId: string | null;
  clientId: string | null;
  /** Set by the claim email list when this went to the client's earlier address (#700). */
  sentToPreviousEmail?: boolean;
}

export interface EmailTemplateInfo {
  templateId: string;
  name: string | null;
  html: string | null;
  imagePath: string | null;
  capturedAt: string | null;
  source: 'snapshot' | 'live' | 'doc';
}

export interface EmailActivitySearchParams {
  recipient?: string;
  subject?: string;
  status?: string;
  engagement?: string;
  date_from?: string;
  date_to?: string;
  claim?: string;
  page?: number;
  limit?: number;
}

export interface SearchResponse {
  success: boolean;
  items: EmailActivityItem[];
  page: number;
  limit: number;
  total: number;
}

interface DetailResponse {
  success: boolean;
  item: EmailActivityItem;
  template: EmailTemplateInfo | null;
}

export interface EmailActivityDetail {
  item: EmailActivityItem;
  template: EmailTemplateInfo | null;
}

interface ClaimActivityResponse {
  success: boolean;
  items: EmailActivityItem[];
}

// API Functions
export async function searchEmailActivity(
  params: EmailActivitySearchParams
): Promise<SearchResponse> {
  const response = await client.get<SearchResponse>('/ui/email-activity', { params });
  if (!response.data.success) {
    throw new Error('Failed to search email activity');
  }
  return response.data;
}

export async function getEmailActivityDetail(id: string): Promise<EmailActivityDetail> {
  const response = await client.get<DetailResponse>(`/ui/email-activity/${id}`);
  if (!response.data.success) {
    throw new Error('Failed to load email');
  }
  return { item: response.data.item, template: response.data.template };
}

export async function getClaimEmailActivity(claimId: string): Promise<EmailActivityItem[]> {
  const response = await client.get<ClaimActivityResponse>(
    `/ui/claims/${encodeURIComponent(claimId)}/email-activity`
  );
  if (!response.data.success) {
    throw new Error('Failed to load claim emails');
  }
  return response.data.items;
}

export interface DraftCostRow {
  id: string;
  messageId: string | null;
  sentAt: string | null;
  description: string;
  recipient: string | null;
  status: string | null;
  claimId: string | null;
  scope: 'claim' | 'client_email' | 'other_claim';
  units: 1;
  rate: string;
  included: boolean;
  sentToPreviousEmail?: boolean;
}

export interface DraftCostData {
  success: true;
  claimId: string;
  generatedAt: string;
  rows: DraftCostRow[];
  recordedUnits: number | null;
  recordedHistory: { timestamp?: string; reason?: string; delta?: number; total?: number; source?: string }[];
  draftUnits: number;
  difference: number | null;
  /** Earlier addresses left out because another client uses them now (#700). */
  excludedAddresses?: number;
}

export async function getDraftCostBreakdown(claimId: string): Promise<DraftCostData> {
  const reference = claimId.trim().toUpperCase();
  const response = await client.get<DraftCostData>(`/ui/claims/${encodeURIComponent(reference)}/draft-cost-breakdown`);
  if (!response.data.success || !Array.isArray(response.data.rows) || response.data.claimId !== reference) {
    throw new Error('Failed to load draft costs breakdown');
  }
  return response.data;
}
